// src/components/Warehouses/WarehouseProducts/ProductTransactions/index.js

import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../../../UserContext";
import toast from "react-hot-toast";
import {
  FaTrash,
  FaPlus,
  FaArrowLeft,
  FaRegEdit,
  FaBoxOpen,
  FaClipboardList,
  FaBuilding,
  FaTag,
  FaCalendarAlt,
} from "react-icons/fa";
// import { Pagination } from 'react-bootstrap'; // Removed pagination import
import "./style.css";

// --- Reusable Delete Confirmation Modal ---
const DeleteModal = ({ isOpen, onClose, onConfirm, itemName, t }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-container">
      <div className="modal-box">
        <h2 className="modal-title">{t("Confirm Deletion")}</h2>
        <p className="modal-confirmation-text">
          {t("Are you sure you want to delete")} <strong>"{itemName}"</strong>?
          <br />
          {t("This action cannot be undone.")}
        </p>
        <div className="modal-buttons">
          <button type="button" className="button-secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button type="button" className="button-danger" onClick={onConfirm}>
            {t("Delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Product Card Component ---
const ProductCard = ({ product, onEdit, onDelete, warehouseId }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const handleCardClick = () => {
    // This is the new navigation handler - pass warehouseId for transferred products
    navigate(`/inventory/product/${product.id}/transactions`, {
      state: { warehouseId: parseInt(warehouseId) },
    });
  };

  const displayName =
    i18n.language === "ar" ? product.nameAr : product.nameEn || product.nameAr;

  const getTypeName = (inventoryType) => {
    if (!inventoryType) return t("N/A");
    return i18n.language === "ar"
      ? inventoryType.nameAr
      : inventoryType.nameEn || inventoryType.nameAr;
  };

  const getExpirationStatus = (timestamp) => {
    if (!timestamp) return { text: t("N/A"), status: "normal" };

    const expirationDate = new Date(timestamp);
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    const formattedDate = expirationDate.toLocaleDateString(i18n.language);

    if (expirationDate < today) {
      return { text: `${t("Expired")}: ${formattedDate}`, status: "expired" };
    } else if (expirationDate < thirtyDaysFromNow) {
      return {
        text: `${t("Expires soon")}: ${formattedDate}`,
        status: "warning",
      };
    } else {
      return { text: formattedDate, status: "normal" };
    }
  };
  const expirationInfo = getExpirationStatus(product.expirationDate);

  return (
    <div className="product-card" onClick={handleCardClick}>
      <div className="product-card-main-content">
        <div className="product-card-header">
          <h3 className="product-name" title={displayName}>
            {displayName}
          </h3>
          <div className="product-quantity-display">
            <span>{product.currentBalance}</span>
            <strong>{product.unit}</strong>
          </div>
        </div>
        <div className="product-card-details">
          <div className="info-row">
            <FaTag className="info-icon" />
            <span>{getTypeName(product.inventoryType)}</span>
          </div>
          <div className="info-row">
            <FaBuilding className="info-icon" />
            <span>{product.producer || t("N/A")}</span>
          </div>
          <div className={`info-row expiration-info ${expirationInfo.status}`}>
            <FaCalendarAlt className="info-icon" />
            <span>{expirationInfo.text}</span>
          </div>
          <div className="info-row description">
            <FaClipboardList className="info-icon" />
            <span>{product.details || t("N/A")}</span>
          </div>
        </div>
      </div>
      <div className="product-card-actions">
        <button
          className="action-button"
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click
            onEdit(product.id);
          }}
          aria-label={t("Edit")}
        >
          <FaRegEdit /> {t("Edit")}
        </button>
        <button
          className="action-button delete"
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click
            onDelete(product);
          }}
          aria-label={t("Delete")}
        >
          <FaTrash /> {t("Delete")}
        </button>
      </div>
    </div>
  );
};

// --- Main Warehouse Products Component ---
const WarehouseProducts = () => {
  const navigate = useNavigate();
  const { warehouseId } = useParams();
  const [t, i18n] = useTranslation();
  const { token, userData } = useContext(UserContext);

  const [products, setProducts] = useState([]);
  const [warehouse, setWarehouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [currentPage, setCurrentPage] = useState(1); // Pagination state removed
  // const productsPerPage = 12; // Pagination state removed

  const [productToDelete, setProductToDelete] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  // Fetch warehouse details
  useEffect(() => {
    const fetchWarehouseDetails = async () => {
      if (!warehouseId || !token || !userData?.Employee?.farmId) return;

      try {
        const response = await axios.get(
          `api/inventoryLocation/findByFarmId/${userData.Employee.farmId}`,
          {
            headers: { Authorization: token },
          }
        );

        if (response.data?.items) {
          const warehouseData = response.data.items.find(
            w => w.id === parseInt(warehouseId)
          );
          setWarehouse(warehouseData);
        }
      } catch (error) {
        console.error("Error fetching warehouse details:", error);
      }
    };

    fetchWarehouseDetails();
  }, [warehouseId, token, userData]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!warehouseId || !token) return;
      setLoading(true);
      setError(null);

      try {
        // First try the original endpoint that works without CORS issues
        const response = await axios.get(
          `api/inventoryItemDetails/findByLocationId/${warehouseId}`,
          {
            headers: { Authorization: `${token}` },
          }
        );

        let baseProducts = [];
        if (response.data && response.data.items) {
          baseProducts = response.data.items || [];
        }

        // Try to get allocations to find transferred products (if CORS allows)
        // But only add products that aren't already in the base list to avoid duplicates
        try {
          const allocationResponse = await axios.get(
            `api/inventoryItemsAllocation/location/${warehouseId}`,
            {
              headers: { Authorization: `${token}` },
            }
          );

          if (allocationResponse.data && allocationResponse.data.items) {
            const allocatedProducts = allocationResponse.data.items || [];

            // Create a map of base products by ID for quick lookup
            const baseProductMap = new Map(baseProducts.map((p) => [p.id, p]));

            // Add or update products from allocation endpoint
            allocatedProducts.forEach((allocProduct) => {
              if (!baseProductMap.has(allocProduct.id)) {
                // This is a transferred product not in the base list
                baseProductMap.set(allocProduct.id, {
                  ...allocProduct,
                  locationId: parseInt(warehouseId),
                });
              } else {
                // Update existing product with allocation data (e.g., currentBalance)
                const existing = baseProductMap.get(allocProduct.id);
                baseProductMap.set(allocProduct.id, {
                  ...existing,
                  ...allocProduct,
                  locationId: parseInt(warehouseId),
                });
              }
            });

            baseProducts = Array.from(baseProductMap.values());
          }
        } catch (allocErr) {
          // If allocation endpoint fails (CORS), just use base products
          console.log(
            "Allocation endpoint not accessible, using base products only"
          );
        }

        // Set locationId for all products to current warehouse
        const productsWithCorrectLocation = baseProducts.map((product) => ({
          ...product,
          locationId: parseInt(warehouseId),
        }));

        setProducts(productsWithCorrectLocation);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(t("Error fetching products"));
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [warehouseId, token, t]);

  // Pagination logic removed

  // --- Delete Handlers ---
  const openDeleteModal = (product) => {
    setProductToDelete(product);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;

    const deletePromise = new Promise(async (resolve, reject) => {
      try {
        // Delete the product
        await axios.delete(`api/inventoryItemDetails/${productToDelete.id}`, {
          headers: { Authorization: `${token}` },
        });

        // Also try to remove allocation if this is in a specific warehouse
        // This ensures transferred products are properly removed from the warehouse
        try {
          await axios.delete(
            `api/inventoryItemsAllocation/${productToDelete.id}/${warehouseId}`,
            { headers: { Authorization: `${token}` } }
          );
        } catch (allocErr) {
          // Allocation deletion might fail if endpoint doesn't exist or product wasn't allocated
          console.log(
            "Allocation removal attempted but not critical:",
            allocErr
          );
        }

        setProducts((prevProducts) =>
          prevProducts.filter((p) => p.id !== productToDelete.id)
        );
        setDeleteModalOpen(false);
        setProductToDelete(null);
        resolve(t("Product deleted successfully."));
      } catch (error) {
        console.error("Error deleting product:", error);
        reject(t("Failed to delete product."));
      }
    });

    toast.promise(deletePromise, {
      loading: t("Deleting product..."),
      success: (message) => message,
      error: (message) => message,
    });
  };

  // --- Navigation Handlers ---
  const handleEditProduct = (productId) => {
    // Find the product data to pass to the edit form
    const productToEdit = products.find((p) => p.id === productId);

    // Navigate to the edit route with product data in state for pre-population
    navigate(`/edit-product/${productId}`, {
      state: {
        product: productToEdit,
        warehouseId: warehouseId, // Include warehouse context
      },
    });
  };

  const getProductNameForModal = (product) => {
    if (!product) return "";
    return i18n.language === "ar"
      ? product.nameAr
      : product.nameEn || product.nameAr;
  };

  const getWarehouseDisplayName = (warehouse) => {
    if (!warehouse) return "";
    return i18n.language === "ar"
      ? warehouse.nameAr || warehouse.nameEn || warehouse.name
      : warehouse.nameEn || warehouse.nameAr || warehouse.name;
  };

  const handleAddProductClick = () => {
    navigate("/add-product", {
      state: {
        checkboxChecked: true,
        warehouseId: parseInt(warehouseId),
        warehouseName: getWarehouseDisplayName(warehouse),
        fromWarehouse: true
      }
    });
  };

  return (
    <div className="warehouse-products-container">
      <header className="view-header">
        <div className="header-left-content">
          <button
            className="button-icon-only"
            onClick={() => navigate(-1)}
            aria-label={t("Go back")}
          >
            <FaArrowLeft />
          </button>
          <div>
            <h1 className="view-header-title">{t("Warehouse Products")}</h1>
            <p className="view-header-subtitle">
              {products.length} {t("Products")}
            </p>
          </div>
        </div>
        <button
          className="button-primary"
          onClick={handleAddProductClick}
        >
          <FaPlus /> {t("Add Product")}
        </button>
      </header>

      {loading ? (
        <div>{t("Loading...")}</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : products.length === 0 ? (
        <div className="no-data-message">
          {t("No products found for this warehouse.")}
        </div>
      ) : (
        <div className="products-grid-container">
          {/* Render all products directly, not just currentProducts */}
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onEdit={handleEditProduct}
              onDelete={openDeleteModal}
              warehouseId={warehouseId}
            />
          ))}
        </div>
      )}

      {/* Pagination component removed */}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemName={getProductNameForModal(productToDelete)}
        t={t}
      />
    </div>
  );
};

export default WarehouseProducts;
